import { useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import type { Point } from 'react-easy-crop';
import { Check, RotateCcw, ZoomIn, ZoomOut } from 'lucide-react';

import { getCroppedImage, type CropArea } from '@/lib/crop-image';
import { cn } from '@/lib/utils';

interface ImageCropperProps {
  /** Object URL or data URL of the source image */
  imageSrc: string;
  /** Called with the cropped File when the user confirms */
  onCropDone: (croppedFile: File, croppedPreviewUrl: string) => void;
  /** Called when the user cancels cropping */
  onCancel: () => void;
}

/**
 * Full-area image cropper with 4:5 aspect ratio (polaroid format).
 * Supports mouse drag, scroll-wheel zoom, and mobile pinch-to-zoom.
 */
export function ImageCropper({ imageSrc, onCropDone, onCancel }: ImageCropperProps) {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(null);
  const [processing, setProcessing] = useState(false);

  const onCropComplete = useCallback((_croppedArea: CropArea, croppedPixels: CropArea) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!croppedAreaPixels) return;
    setProcessing(true);
    try {
      const croppedFile = await getCroppedImage(imageSrc, croppedAreaPixels);
      const previewUrl = URL.createObjectURL(croppedFile);
      onCropDone(croppedFile, previewUrl);
    } catch (err) {
      console.error('Crop failed:', err);
      setProcessing(false);
    }
  }, [imageSrc, croppedAreaPixels, onCropDone]);

  const handleReset = useCallback(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  }, []);

  return (
    <div className="space-y-3">
      {/* Cropper area — inside a polaroid-white frame */}
      <div
        className="relative w-full aspect-[4/5] overflow-hidden bg-cream-dark"
        style={{ touchAction: 'none' }}
      >
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          aspect={4 / 5}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
          minZoom={1}
          maxZoom={4}
          showGrid={false}
          cropSize={undefined}
          style={{
            containerStyle: {
              width: '100%',
              height: '100%',
              backgroundColor: 'rgb(var(--pol-bg-dark))',
            },
            cropAreaStyle: {
              border: '2px solid rgba(255,255,255,0.6)',
              borderRadius: '0px',
            },
            mediaStyle: {},
          }}
        />

        {/* Zoom hint — fades out after first interaction */}
        <div className="absolute bottom-2 left-0 right-0 flex justify-center pointer-events-none">
          <span className="font-hand text-[12px] text-white/60 bg-black/30 px-2 py-0.5 backdrop-blur-sm">
            Pinch or scroll to zoom · Drag to pan
          </span>
        </div>
      </div>

      {/* Controls */}
      <div className="space-y-2">
        {/* Zoom slider row */}
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setZoom((z) => Math.max(1, z - 0.25))}
            className="w-7 h-7 flex-shrink-0 flex items-center justify-center text-pol-text-secondary hover:text-pol-text transition-colors active:scale-90"
            aria-label="Zoom out"
          >
            <ZoomOut size={15} />
          </button>

          <input
            type="range"
            min={1}
            max={4}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className={cn(
              'flex-1 h-1.5 appearance-none bg-cream-dark rounded-full outline-none',
              '[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-pol-pink [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-sm',
              '[&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:bg-pol-pink [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-0',
            )}
          />

          <button
            type="button"
            onClick={() => setZoom((z) => Math.min(4, z + 0.25))}
            className="w-7 h-7 flex-shrink-0 flex items-center justify-center text-pol-text-secondary hover:text-pol-text transition-colors active:scale-90"
            aria-label="Zoom in"
          >
            <ZoomIn size={15} />
          </button>
        </div>

        {/* Action row — reset left, skip/crop right */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={handleReset}
            className="flex items-center gap-1 font-hand text-[13px] text-pol-text-secondary hover:text-pol-text transition-colors active:scale-90 px-1 py-0.5"
            aria-label="Reset crop"
          >
            <RotateCcw size={13} />
            <span>Reset</span>
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={processing}
              className="font-hand text-[14px] text-pol-text-secondary hover:text-pol-text transition-colors px-2 py-1"
            >
              Skip
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={processing}
              className={cn(
                'flex items-center gap-1 font-hand text-[14px] font-bold text-pol-text px-3 py-1.5',
                'bg-pol-pink/20 hover:bg-pol-pink/30 transition-colors active:scale-95',
                'disabled:opacity-50 disabled:cursor-not-allowed',
              )}
            >
              <Check size={14} />
              {processing ? 'Cropping...' : 'Crop'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
